// #3088 - the REVIEW button's local half. Offline-first like every other scribe
// write: pressing REVIEW never waits on the network. It
//   1. enqueues a `requestRideReview` outbox op (FIFO behind the ride's own
//      sheet ops, so the server sees the sheet in whatever state it was in
//      when REVIEW was pressed, then the request), and
//   2. stamps an optimistic reviewRequest on the cached ride so the ride list
//      shows the In Review badge at once, offline included.
// sync.js's applyOp drains the op against POST /ride/:rideId/review (which is
// idempotent, so a replay is safe) and writes the server's reviewRequest back
// onto the cached ride.
//
// overlayPendingReviewRequests is the guard for the one race that matters: a
// revalidation (useRidesForClass, hydrate, ScoringForm's ride fetch) that
// lands while the op is still queued would otherwise put a server ride with no
// reviewRequest over the optimistic cache row and the badge would vanish until
// the op drained. Any ride with a still-queued review op is re-stamped here.
import { enqueue, peekOutbox, rides } from "./db";

export const REVIEW_OP_TYPE = "requestRideReview";

export function reviewOpId(rideId) {
  return `review:${rideId}`;
}

export function isActiveReviewRequest(ride) {
  return ride?.reviewRequest?.active === true;
}

/**
 * @param {object} args
 * @param {string} args.rideId - the EFFECTIVE ride id (a manual ride's
 *   serverId once it has one, else its temp id)
 * @param {boolean} [args.isTempRide] - true when rideId is a not-yet-synced
 *   manual ride's temp id, so sync.js holds the op until createRide has
 *   drained and remapTempRide rewrites payload.rideId
 * @param {string} [args.comment]
 */
export async function queueRideReview({ rideId, isTempRide = false, comment }) {
  const trimmed = (comment ?? "").trim();
  const requestedAt = new Date().toISOString();
  const op = await enqueue({
    opId: reviewOpId(rideId),
    type: REVIEW_OP_TYPE,
    // Bare rideId, like a utility-event op: manualRides.js's discard guard
    // matches pending ops on entityKey === tempId, so a manual ride with an
    // undrained review request is refused/cleaned up like any other dependent.
    entityKey: rideId,
    tempId: isTempRide ? rideId : null,
    payload: { rideId, comment: trimmed || null, requestedAt },
  });
  const cached = await rides.get(rideId);
  if (cached && !isActiveReviewRequest(cached)) {
    await rides.put({
      ...cached,
      reviewRequest: { active: true, comment: trimmed || null, at: requestedAt, pending: true },
    });
  }
  return op;
}

// Re-stamps the optimistic reviewRequest on any ride whose review op is still
// in the outbox and whose own row does not carry an active request yet.
export async function overlayPendingReviewRequests(rows) {
  if (!rows?.length) return rows;
  const ops = (await peekOutbox()).filter((op) => op.type === REVIEW_OP_TYPE);
  if (!ops.length) return rows;
  const byRide = new Map(ops.map((op) => [op.payload?.rideId, op]));
  return rows.map((row) => {
    const op = byRide.get(row.id);
    if (!op || isActiveReviewRequest(row)) return row;
    return {
      ...row,
      reviewRequest: {
        active: true,
        comment: op.payload?.comment ?? null,
        at: op.payload?.requestedAt ?? null,
        pending: true,
      },
    };
  });
}
